/**
 * Devices Management Page
 * Admin-sida för att visa och söka efter enheter
 */

import { useDeferredValue, useEffect, useState, useCallback, useMemo } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import { devicesService, type Device, type SimStatus } from '@/services/devices/devicesService';
import toast from 'react-hot-toast';
import { 
 MagnifyingGlassIcon, 
 XMarkIcon, 
 SignalIcon,
 ArrowPathIcon,
 CheckCircleIcon,
 XCircleIcon,
 ChevronDownIcon,
 ArrowLeftIcon,
 DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';

// SIM-status konfiguration
const simStatusConfig: Record<string, { labelKey: string; color: string; bgColor: string }> = {
 ACTIVATED: { labelKey: 'devices.simStatusActivated', color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
 ACTIVATION_READY: { labelKey: 'devices.simStatusReady', color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
 DEACTIVATED: { labelKey: 'devices.simStatusInactive', color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30' },
 INVENTORY: { labelKey: 'devices.simStatusInventory', color: 'text-textSecondary', bgColor: 'bg-bgBase' },
 RETIRED: { labelKey: 'devices.simStatusRetired', color: 'text-textSecondary', bgColor: 'bg-bgBase' },
 TEST_READY: { labelKey: 'devices.simStatusTest', color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
};

const validStatuses = ['ACTIVATED', 'ACTIVATION_READY', 'DEACTIVATED', 'INVENTORY', 'RETIRED', 'TEST_READY'];

const DevicesManagement = () => {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const [allDevices, setAllDevices] = useState<Device[]>([]);
 const [initialLoading, setInitialLoading] = useState(true);
 const [searchTerm, setSearchTerm] = useState('');
 const deferredSearchTerm = useDeferredValue(searchTerm);
 const [currentPage, setCurrentPage] = useState(1);
 const pageSize = 100;
 const [lastSyncTimestamp, setLastSyncTimestamp] = useState<string | null>(null);
 const [syncTriggering, setSyncTriggering] = useState(false);

 // Modal state
 const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
 const [simStatus, setSimStatus] = useState<SimStatus | null>(null);
 const [simLoading, setSimLoading] = useState(false);
 const [simChanging, setSimChanging] = useState(false);
 const [showStatusDropdown, setShowStatusDropdown] = useState(false);

 // Ladda alla devices en gång vid start
 useEffect(() => {
 loadAllDevices();
 loadLastSyncTimestamp();
 }, []);

 const loadAllDevices = async () => {
 try {
 setInitialLoading(true);
 const devicesData = await devicesService.getAllDevices(); // Ladda alla utan sökterm
 setAllDevices(devicesData);
 } catch (error: any) {
 toast.error(t('common.error') + ': ' + (error.message || 'Unknown error'));
 } finally {
 setInitialLoading(false);
 }
 };

 const loadLastSyncTimestamp = async () => {
 try {
 const timestamp = await devicesService.getLastSyncTimestamp();
 setLastSyncTimestamp(timestamp);
 } catch {
 // Tyst fel - visa inte toast för detta
 }
 };

 const handleTriggerFotaSync = async () => {
 if (syncTriggering) return;
 setSyncTriggering(true);
 try {
 await devicesService.triggerFotaDevicesSync();
 toast.success(
 <div>
 <div className="font-medium">{t('devices.syncStarted')}</div>
 <div className="text-sm mt-1 opacity-90">{t('devices.syncStartedBody')}</div>
 </div>,
 { duration: 9000 }
 );
 window.setTimeout(() => {
 void loadLastSyncTimestamp();
 void loadAllDevices();
 }, 8000);
 } catch (e: unknown) {
 const err = e as { response?: { status?: number; data?: { error?: string } } };
 if (err?.response?.status === 403) {
 toast.error(t('devices.syncForbidden'));
 } else {
 const detail = err?.response?.data?.error;
 toast.error(detail ? `${t('devices.syncFailed')}: ${detail}` : t('devices.syncFailed'));
 }
 } finally {
 setSyncTriggering(false);
 }
 };

 const formatSyncTimestamp = (timestamp: string | null): string => {
 if (!timestamp) return '';
 try {
 const date = new Date(timestamp);
 if (isNaN(date.getTime())) {
 return '';
 }
 // Format: YYYY-MM-DD HH:mm
 const year = date.getFullYear();
 const month = String(date.getMonth() + 1).padStart(2, '0');
 const day = String(date.getDate()).padStart(2, '0');
 const hours = String(date.getHours()).padStart(2, '0');
 const minutes = String(date.getMinutes()).padStart(2, '0');
 return `${year}-${month}-${day} ${hours}:${minutes}`;
 } catch {
 return '';
 }
 };

 /** Formatera FOTA last_seen (senast sedd) för visning i tabell */
 const formatLastSeen = (lastSeen: string): string => {
 try {
 const date = new Date(lastSeen);
 if (isNaN(date.getTime())) return '';
 const year = date.getFullYear();
 const month = String(date.getMonth() + 1).padStart(2, '0');
 const day = String(date.getDate()).padStart(2, '0');
 const hours = String(date.getHours()).padStart(2, '0');
 const minutes = String(date.getMinutes()).padStart(2, '0');
 return `${year}-${month}-${day} ${hours}:${minutes}`;
 } catch {
 return '';
 }
 };

 // Filtrera devices lokalt baserat på searchTerm - omedelbar respons utan API-anrop
 const filteredDevices = useMemo(() => {
 if (!deferredSearchTerm.trim()) {
 return allDevices;
 }

 const term = deferredSearchTerm.toLowerCase().trim();
 return allDevices.filter(device => {
 const onlineOffline = device.fota_online === true ? 'online' : device.fota_online === false ? 'offline' : '';
 return (
 device.imei?.toLowerCase().includes(term) ||
 device.model?.toLowerCase().includes(term) ||
 device.current_firmware?.toLowerCase().includes(term) ||
 device.current_configuration?.toLowerCase().includes(term) ||
 device.gsm_number?.toLowerCase().includes(term) ||
 (onlineOffline && onlineOffline.includes(term))
 );
 });
 }, [allDevices, deferredSearchTerm]);

 const totalPages = useMemo(
   () => Math.max(1, Math.ceil(filteredDevices.length / pageSize)),
   [filteredDevices.length, pageSize]
 );

 const pagedDevices = useMemo(() => {
   const start = (currentPage - 1) * pageSize;
   return filteredDevices.slice(start, start + pageSize);
 }, [filteredDevices, currentPage, pageSize]);

 useEffect(() => {
   setCurrentPage(1);
 }, [deferredSearchTerm, allDevices.length]);

 useEffect(() => {
   if (currentPage > totalPages) {
     setCurrentPage(totalPages);
   }
 }, [currentPage, totalPages]);

 // Handle search input change - direkt uppdatering för omedelbar respons
 const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
 setSearchTerm(e.target.value);
 }, []);

 // Öppna enhetsdetaljer och hämta SIM-status
 const handleDeviceClick = async (device: Device) => {
 setSelectedDevice(device);
 setSimStatus(null);
 setShowStatusDropdown(false);
 
 if (device.iccid) {
 setSimLoading(true);
 try {
 const status = await devicesService.getSimStatus(device.imei);
 setSimStatus(status);
 } catch {
 // Error fetching SIM status - handled silently
 } finally {
 setSimLoading(false);
 }
 }
 };

 // Ändra SIM-status
 const handleSimStatusChange = async (newStatus: string) => {
 if (!selectedDevice) return;
 
 setSimChanging(true);
 setShowStatusDropdown(false);
 
 try {
 const result = await devicesService.updateSimStatus(selectedDevice.imei, newStatus);
 if (result.success) {
 toast.success(t('devices.simStatusChanged'));
 setSimStatus(prev => prev ? { ...prev, status: newStatus } : null);
 } else {
 toast.error(result.message || t('devices.couldNotChangeStatus'));
 }
 } catch (error: any) {
 toast.error(error.message || t('devices.statusChangeError'));
 } finally {
 setSimChanging(false);
 }
 };

 // Formatera bytes
 const formatBytes = (bytes?: number): string => {
 if (!bytes) return '0 B';
 if (bytes < 1024) return `${bytes} B`;
 if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
 if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
 return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
 };

 if (initialLoading) {
 return (
 <div className="p-4 bg-bgContainer rounded-lg h-full flex items-center justify-center">
 <div className="text-textSecondary">{t('common.loading')}</div>
 </div>
 );
 }

 return (
 <div className="p-4 bg-bgContainer rounded-lg h-full">
 <div className="mb-6">
 <div className="flex items-center gap-3 mb-4">
 <button onClick={() => navigate(ROUTES.SYSTEM_ADMINISTRATION)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
 <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
 </button>
 <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
 <DevicePhoneMobileIcon className="w-6 h-6 text-primary" />
 {t('admin.devices')}
 </h1>
 </div>
 
 {/* Sökfält */}
 <div className="relative">
 <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
 <MagnifyingGlassIcon className="h-5 w-5 text-textSecondary" />
 </div>
 <input
 type="text"
 placeholder={t('devices.searchPlaceholder')}
 value={searchTerm}
 onChange={handleSearchChange}
 className="block w-full pl-10 pr-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault placeholder-textSecondary focus:outline-none focus:ring-2 focus:ring-bgPrimary focus:border-transparent"
 autoComplete="off"
 />
 </div>
 
 <div className="mt-2 flex flex-wrap justify-between items-center gap-2">
 <span className="text-sm text-textSecondary">
 {t('devices.showingResults').replace('{count}', filteredDevices.length.toString()).replace('{total}', allDevices.length.toString())}
 </span>
 <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
 <span className="text-sm text-textSecondary">
 {lastSyncTimestamp ? (
 <>
 {t('devices.lastSync')}: {formatSyncTimestamp(lastSyncTimestamp)}
 </>
 ) : (
 <span className="text-textSecondary opacity-50">{t('devices.lastSync')}: -</span>
 )}
 </span>
 <button
 type="button"
 onClick={() => void handleTriggerFotaSync()}
 disabled={syncTriggering}
 className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-border bg-bgElevated text-textDefault hover:bg-bgLight disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
 title={t('devices.syncStartedBody')}
 >
 <ArrowPathIcon className={`w-4 h-4 shrink-0 ${syncTriggering ? 'animate-spin' : ''}`} />
 {t('devices.syncNow')}
 </button>
 </div>
 </div>
 </div>

 {/* Devices Tabell */}
 <div className="bg-bgElevated rounded-lg border border-border overflow-hidden">
 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-border">
 <thead className="bg-bgLight">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('devices.imei')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('devices.model')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('devices.currentFirmware')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('devices.fotaStatus')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('devices.seenAt')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('devices.currentConfiguration')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('devices.simStatus')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('devices.gsmNumber')}
 </th>
 </tr>
 </thead>
 <tbody className="bg-bgElevated divide-y divide-border">
 {filteredDevices.length === 0 ? (
 <tr>
 <td colSpan={8} className="px-6 py-4 text-center text-textSecondary">
 {searchTerm ? t('devices.noResults') : t('devices.noDevices')}
 </td>
 </tr>
 ) : (
 pagedDevices.map((device) => {
 const statusInfo = device.sim_status ? simStatusConfig[device.sim_status] : null;
 return (
 <tr 
 key={device.id} 
 onClick={() => handleDeviceClick(device)}
 className="hover:bg-bgLight transition-colors cursor-pointer"
 >
 <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-textDefault">
 {device.imei || '-'}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textSecondary">
 {device.model || '-'}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textSecondary">
 {device.current_firmware || '-'}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm">
 {device.fota_online !== undefined ? (
 <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
 device.fota_online
 ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
 : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
 }`}>
 {device.fota_online ? t('devices.online') : t('devices.offline')}
 </span>
 ) : (
 <span className="text-textSecondary">-</span>
 )}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textSecondary" title={device.last_seen ? formatLastSeen(device.last_seen) : undefined}>
 {device.last_seen ? formatLastSeen(device.last_seen) : '-'}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textSecondary">
 {device.current_configuration || '-'}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm">
 {device.sim_status && statusInfo ? (
 <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color}`}>
 {t(statusInfo.labelKey)}
 </span>
 ) : device.iccid ? (
 <span className="text-textSecondary">-</span>
 ) : (
 <span className="text-textTertiary italic">{t('devices.noSim')}</span>
 )}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textSecondary">
 {device.gsm_number || '-'}
 </td>
 </tr>
 );
 })
 )}
 </tbody>
 </table>
 </div>
 <div className="px-6 py-3 border-t border-border flex items-center justify-between text-sm">
 <span className="text-textSecondary">
 {(t('common.page') || 'Sida')} {currentPage} / {totalPages}
 </span>
 <div className="flex items-center gap-2">
 <button
 type="button"
 onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
 disabled={currentPage === 1}
 className="px-3 py-1 rounded border border-border bg-bgContainer text-textDefault disabled:opacity-50"
 >
 {t('common.previous') || 'Föregående'}
 </button>
 <button
 type="button"
 onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
 disabled={currentPage >= totalPages}
 className="px-3 py-1 rounded border border-border bg-bgContainer text-textDefault disabled:opacity-50"
 >
 {t('common.next') || 'Nästa'}
 </button>
 </div>
 </div>
 </div>

 {/* Device Detail Modal */}
 {selectedDevice && (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedDevice(null)}>
 <div 
 className="bg-bgElevated rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden"
 onClick={(e) => e.stopPropagation()}
 >
 {/* Header */}
 <div className="p-6 border-b border-border flex justify-between items-start">
 <div>
 <h2 className="text-xl font-bold text-textDefault">
 {t('devices.deviceDetailsTitle')}
 </h2>
 <p className="text-sm text-textSecondary font-mono mt-1">{selectedDevice.imei}</p>
 </div>
 <button
 onClick={() => setSelectedDevice(null)}
 className="p-2 hover:bg-bgHover rounded-lg transition-colors"
 >
 <XMarkIcon className="w-5 h-5 text-textSecondary" />
 </button>
 </div>

 {/* Content */}
 <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] space-y-4">
 {/* Device Info */}
 <div className="space-y-2">
 <div className="flex justify-between">
 <span className="text-textSecondary">{t('devices.model')}</span>
 <span className="text-textDefault">{selectedDevice.model || '-'}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-textSecondary">{t('devices.firmware')}</span>
 <span className="text-textDefault text-sm">{selectedDevice.current_firmware || '-'}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-textSecondary">{t('devices.configuration')}</span>
 <span className="text-textDefault text-sm">{selectedDevice.current_configuration || '-'}</span>
 </div>
 {selectedDevice.fota_online !== undefined && (
 <div className="flex justify-between items-center">
 <span className="text-textSecondary">{t('devices.fotaStatus')}</span>
 <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
 selectedDevice.fota_online
 ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
 : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
 }`}>
 {selectedDevice.fota_online ? t('devices.online') : t('devices.offline')}
 </span>
 </div>
 )}
 {selectedDevice.last_seen && (
 <div className="flex justify-between">
 <span className="text-textSecondary">{t('devices.lastSeen')}</span>
 <span className="text-textDefault text-sm">{formatLastSeen(selectedDevice.last_seen)}</span>
 </div>
 )}
 {selectedDevice.gsm_number && (
 <div className="flex justify-between">
 <span className="text-textSecondary">{t('devices.gsmNumber')}</span>
 <span className="text-textDefault font-mono">{selectedDevice.gsm_number}</span>
 </div>
 )}
 {selectedDevice.iccid && (
 <div className="flex justify-between">
 <span className="text-textSecondary">ICCID</span>
 <span className="text-textDefault font-mono text-xs">{selectedDevice.iccid}</span>
 </div>
 )}
 </div>

 {/* SIM Status Section */}
 {selectedDevice.iccid && (
 <div className="border-t border-border pt-4">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-lg font-semibold text-textDefault flex items-center gap-2">
 <SignalIcon className="w-5 h-5" />
 {t('devices.simCardJasper')}
 </h3>
 {!simLoading && (
 <button
 onClick={() => handleDeviceClick(selectedDevice)}
 className="p-1 hover:bg-bgHover rounded transition-colors"
 title={t('common.refresh')}
 >
 <ArrowPathIcon className="w-4 h-4 text-textSecondary" />
 </button>
 )}
 </div>

 {simLoading ? (
 <div className="flex items-center justify-center py-4">
 <ArrowPathIcon className="w-6 h-6 animate-spin text-blue-500" />
 <span className="ml-2 text-textSecondary">
 {t('devices.fetchingSimStatus')}
 </span>
 </div>
 ) : simStatus ? (
 <div className="space-y-3">
 {/* Status med dropdown */}
 <div className="flex justify-between items-center">
 <span className="text-textSecondary">Status</span>
 <div className="relative">
 <button
 onClick={() => setShowStatusDropdown(!showStatusDropdown)}
 disabled={simChanging}
 className={`flex items-center gap-2 px-3 py-1 rounded-full ${
 simStatusConfig[simStatus.status]?.bgColor || 'bg-bgBase'
 } ${simStatusConfig[simStatus.status]?.color || 'text-textSecondary'} hover:opacity-80 transition-opacity`}
 >
 {simChanging ? (
 <ArrowPathIcon className="w-4 h-4 animate-spin" />
 ) : simStatus.status === 'ACTIVATED' ? (
 <CheckCircleIcon className="w-4 h-4" />
 ) : simStatus.status === 'DEACTIVATED' ? (
 <XCircleIcon className="w-4 h-4" />
 ) : null}
 <span className="text-sm font-medium">
 {simStatusConfig[simStatus.status]?.labelKey
 ? t(simStatusConfig[simStatus.status].labelKey)
 : simStatus.status
 }
 </span>
 <ChevronDownIcon className="w-4 h-4" />
 </button>

 {showStatusDropdown && (
 <div className="absolute right-0 mt-2 w-48 bg-bgElevated rounded-lg shadow-lg border border-border z-10">
 {validStatuses.map((status) => {
 const info = simStatusConfig[status];
 return (
 <button
 key={status}
 onClick={() => handleSimStatusChange(status)}
 disabled={status === simStatus.status}
 className={`w-full text-left px-4 py-2 text-sm hover:bg-bgHover transition-colors first:rounded-t-lg last:rounded-b-lg ${
 status === simStatus.status ? 'opacity-50 cursor-not-allowed' : ''
 }`}
 >
 <span className={info.color}>
 {t(info.labelKey)}
 </span>
 </button>
 );
 })}
 </div>
 )}
 </div>
 </div>

 {/* Övriga SIM-detaljer */}
 {simStatus.msisdn && (
 <div className="flex justify-between">
 <span className="text-textSecondary">MSISDN</span>
 <span className="font-mono text-textDefault">{simStatus.msisdn}</span>
 </div>
 )}
 {simStatus.ratePlan && (
 <div className="flex justify-between">
 <span className="text-textSecondary">{t('devices.ratePlan')}</span>
 <span className="text-textDefault text-sm">{simStatus.ratePlan}</span>
 </div>
 )}
 {simStatus.communicationPlan && (
 <div className="flex justify-between">
 <span className="text-textSecondary">{t('devices.commPlan')}</span>
 <span className="text-textDefault text-sm">{simStatus.communicationPlan}</span>
 </div>
 )}

 {/* Användning */}
 {(simStatus.ctdDataUsage !== undefined || simStatus.ctdSMSUsage !== undefined) && (
 <>
 <div className="border-t border-border my-3" />
 <div className="text-sm text-textSecondary mb-2">
 {t('devices.usageThisPeriod')}
 </div>
 <div className="grid grid-cols-3 gap-2">
 <div className="bg-bgDefault rounded-lg p-2 text-center">
 <div className="text-lg font-semibold text-textDefault">
 {formatBytes(simStatus.ctdDataUsage)}
 </div>
 <div className="text-xs text-textSecondary">Data</div>
 </div>
 <div className="bg-bgDefault rounded-lg p-2 text-center">
 <div className="text-lg font-semibold text-textDefault">
 {simStatus.ctdSMSUsage || 0}
 </div>
 <div className="text-xs text-textSecondary">SMS</div>
 </div>
 <div className="bg-bgDefault rounded-lg p-2 text-center">
 <div className="text-lg font-semibold text-textDefault">
 {simStatus.ctdSessionCount || 0}
 </div>
 <div className="text-xs text-textSecondary">{t('devices.sessions')}</div>
 </div>
 </div>
 </>
 )}

 {simStatus.ipAddress && (
 <div className="flex justify-between">
 <span className="text-textSecondary">IP</span>
 <span className="font-mono text-textDefault text-sm">{simStatus.ipAddress}</span>
 </div>
 )}
 </div>
 ) : (
 <div className="text-center py-4 text-textSecondary">
 {t('devices.couldNotFetchSimStatus')}
 </div>
 )}
 </div>
 )}
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default DevicesManagement;

